<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';

const props = defineProps({
  assistantId: {
    type: [Number, String],
    default: null,
  },
});

const { t } = useI18n();

const dialogRef = ref(null);
const fileInputRef = ref(null);

const prompt = ref('');
const attachedImage = ref(null);
const attachedPreview = ref(null);
const generating = ref(false);
const resultDataUrl = ref('');

const canGenerate = computed(
  () => prompt.value.trim().length > 0 && !generating.value && props.assistantId
);

const open = () => {
  dialogRef.value?.open();
};

const clearAttachment = () => {
  if (attachedPreview.value) URL.revokeObjectURL(attachedPreview.value);
  attachedImage.value = null;
  attachedPreview.value = null;
  if (fileInputRef.value) fileInputRef.value.value = '';
};

const resetState = () => {
  prompt.value = '';
  resultDataUrl.value = '';
  clearAttachment();
};

const openFilePicker = () => {
  fileInputRef.value?.click();
};

const onFileSelected = event => {
  const file = event.target.files?.[0];
  if (!file) return;
  if (!file.type.startsWith('image/')) {
    useAlert(t('CAPTAIN.COPILOT.IMAGE.INVALID_FILE'));
    return;
  }
  attachedImage.value = file;
  attachedPreview.value = URL.createObjectURL(file);
};

const generate = async () => {
  if (!canGenerate.value) return;
  generating.value = true;
  resultDataUrl.value = '';
  try {
    const { data } = await CaptainAssistantAPI.generateImage({
      assistantId: props.assistantId,
      prompt: prompt.value,
      image: attachedImage.value,
    });
    const contentType = data.content_type || 'image/png';
    resultDataUrl.value = `data:${contentType};base64,${data.image}`;
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CAPTAIN.COPILOT.IMAGE.GENERATE_ERROR')
    );
  } finally {
    generating.value = false;
  }
};

const dataUrlToBlob = async () => {
  const response = await fetch(resultDataUrl.value);
  return response.blob();
};

const downloadImage = () => {
  const link = document.createElement('a');
  link.href = resultDataUrl.value;
  link.download = `captain-image-${Date.now()}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const copyImage = async () => {
  try {
    const blob = await dataUrlToBlob();
    // eslint-disable-next-line no-undef
    await navigator.clipboard.write([
      // eslint-disable-next-line no-undef
      new ClipboardItem({ [blob.type]: blob }),
    ]);
    useAlert(t('CAPTAIN.COPILOT.IMAGE.COPIED'));
  } catch (error) {
    useAlert(t('CAPTAIN.COPILOT.IMAGE.COPY_ERROR'));
  }
};

const handleClose = () => {
  resetState();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CAPTAIN.COPILOT.IMAGE.TITLE')"
    :description="t('CAPTAIN.COPILOT.IMAGE.DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
    @close="handleClose"
  >
    <div class="flex flex-col gap-4">
      <label class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.COPILOT.IMAGE.PROMPT_LABEL') }}
        </span>
        <textarea
          v-model="prompt"
          rows="3"
          :placeholder="t('CAPTAIN.COPILOT.IMAGE.PROMPT_PLACEHOLDER')"
          class="w-full reset-base bg-n-alpha-3 px-3 py-2 text-sm border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-blue-11 resize-none text-n-slate-12"
        />
      </label>

      <div class="flex items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          :label="t('CAPTAIN.COPILOT.IMAGE.ATTACH_REFERENCE')"
          icon="i-lucide-image"
          @click="openFilePicker"
        />
        <div v-if="attachedPreview" class="relative">
          <img
            :src="attachedPreview"
            class="h-12 w-12 rounded object-cover border border-n-weak"
            alt=""
          />
          <button
            type="button"
            class="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center rounded-full bg-n-slate-3 text-n-slate-11 hover:text-n-slate-12"
            @click="clearAttachment"
          >
            <i class="i-lucide-x size-3" />
          </button>
        </div>
        <input
          ref="fileInputRef"
          type="file"
          accept="image/*"
          class="hidden"
          @change="onFileSelected"
        />
      </div>

      <Button
        :label="t('CAPTAIN.COPILOT.IMAGE.GENERATE')"
        :is-loading="generating"
        :disabled="!canGenerate"
        icon="i-lucide-sparkles"
        @click="generate"
      />

      <div v-if="generating" class="text-sm text-n-slate-11 text-center py-4">
        {{ t('CAPTAIN.COPILOT.IMAGE.GENERATING') }}
      </div>

      <div v-if="resultDataUrl && !generating" class="flex flex-col gap-3">
        <img
          :src="resultDataUrl"
          class="w-full rounded-lg border border-n-weak"
          alt=""
        />
        <div class="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            :label="t('CAPTAIN.COPILOT.IMAGE.COPY')"
            icon="i-lucide-copy"
            @click="copyImage"
          />
          <Button
            variant="outline"
            size="sm"
            :label="t('CAPTAIN.COPILOT.IMAGE.DOWNLOAD')"
            icon="i-lucide-download"
            @click="downloadImage"
          />
        </div>
      </div>
    </div>
    <template #footer />
  </Dialog>
</template>
